import { formatBaht } from '@xver/utils/number';
import {
    Fragment,
    PropsWithChildren,
    ReactNode,
    memo,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import { Drawer, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
    IconBuildingWarehouse,
    IconCloud,
    IconCurrencyBaht,
    IconDatabase,
    IconFolders,
    IconGift,
    IconKey,
    IconLayoutDashboard,
    IconMailExclamation,
    IconMenu2,
    IconMessageChatbot,
    IconPackage,
    IconServer,
    IconSettings,
    IconTicket,
    IconUser,
    IconUserExclamation,
    IconUserShield,
    IconWallet,
    IconX,
} from '@tabler/icons-react';
import { EmailLevel, KYCLevel, User } from '../../@types/user';
import { Balance, ColorSchemeToggle, Container, LanguageSwitcher } from '../../components/Atoms';
import {
    AccountMenuButton,
    PROJECT_SWITCH_NAVIGATION_STATE,
    ProjectChip,
    ProjectSwitcher,
} from '../../components/Molecules';
import { NavigationListProps } from '../../components/Molecules/NavigationList/NavigationList.types';
import { NavigationSideBar } from '../../components/Organisms';
import { useProjectRequestsQuery } from '../../hooks/useProjectRequestsQuery';
import { useTrialCreditQuery } from '../../hooks/useTrialCredit';
import XVerWhiteLogo from './../../assets/image/xver-white.svg';
import './ConsoleLayout.css';

const NAV_DRAWER_ID = 'console-navigation-drawer';

const CHAT_BUTTON_CLASS =
    'w-full h-full flex items-center justify-center p-2 text-sm rounded-md cursor-pointer border-[0.5px] border-line text-white bg-blue-500/80 hover:bg-blue-500/60 light:bg-[#2563eb] light:hover:bg-[#1d4ed8] transition-all duration-100';

const ChatSupportButton = memo(() => {
    const { t } = useTranslation('common');
    return (
        <button onClick={() => window.$chatwoot?.toggle()} className={CHAT_BUTTON_CLASS}>
            <IconMessageChatbot />
            <span className="ml-2 font-bold text-sm">{t('chatSupport')}</span>
        </button>
    );
});

const LogoImage = memo<{ isDrawer?: boolean }>(({ isDrawer }) => (
    <img
        src={XVerWhiteLogo}
        alt="xver logo"
        width={isDrawer ? '88' : '128'}
        height="auto"
        className={
            isDrawer
                ? 'w-22 h-auto cursor-pointer select-none sm:w-28'
                : 'ml-1 mt-4 w-22 h-auto cursor-pointer select-none sm:w-28'
        }
    />
));

interface ConsoleLayoutProps extends PropsWithChildren {
    user?: User;
}

function ConsoleLayout({ children, user }: ConsoleLayoutProps) {
    const [opened, { open, close }] = useDisclosure(false);
    const { t } = useTranslation('navigation');
    const { t: tNotifications } = useTranslation('notifications');
    const navigate = useNavigate();
    const location = useLocation();
    const mainRef = useRef<HTMLElement>(null);
    const { pendingCount } = useProjectRequestsQuery();
    const [focusProjectSwitcher, setFocusProjectSwitcher] = useState(false);
    const [returnFocusOnClose, setReturnFocusOnClose] = useState(true);

    const handleMenuOpen = useCallback(() => {
        setFocusProjectSwitcher(false);
        setReturnFocusOnClose(true);
        open();
    }, [open]);

    const handleProjectChipClick = useCallback(() => {
        setFocusProjectSwitcher(true);
        setReturnFocusOnClose(true);
        open();
    }, [open]);

    useEffect(() => {
        const state = location.state as Partial<typeof PROJECT_SWITCH_NAVIGATION_STATE> | null;
        if (state?.keepNavigationOpen) return;
        setReturnFocusOnClose(false);
        close();
    }, [location.pathname, location.state, close]);

    const showReminder = useCallback(
        (reminder: {
            id: string;
            title: string;
            message: string;
            action: string;
            to: string;
            icon: ReactNode;
            color: string;
        }) => {
            notifications.show({
                id: reminder.id,
                title: reminder.title,
                icon: reminder.icon,
                color: reminder.color,
                // Shown once per session; it stays until dismissed so keyboard users can
                // reach its action in the notifications portal (SC 2.2.1).
                autoClose: false,
                message: (
                    <>
                        <p>{reminder.message}</p>
                        <button
                            type="button"
                            className="mt-1 min-h-6 inline-flex items-center font-medium underline cursor-pointer"
                            onClick={() => {
                                notifications.hide(reminder.id);
                                navigate(reminder.to);
                            }}
                        >
                            {reminder.action}
                        </button>
                    </>
                ),
            });
        },
        [navigate]
    );

    useEffect(() => {
        if (!user) return;

        const emailFlag = `xver:reminded:email:${user.id}`;
        if (sessionStorage.getItem('xver:reminded:email:suppress')) {
            sessionStorage.removeItem('xver:reminded:email:suppress');
            sessionStorage.setItem(emailFlag, '1');
        }
        if (user.verifyEmailAddress !== EmailLevel.E2 && !sessionStorage.getItem(emailFlag)) {
            sessionStorage.setItem(emailFlag, '1');
            showReminder({
                id: 'reminder-email',
                title: tNotifications('auth.verifyEmailReminderTitle'),
                message: tNotifications('auth.verifyEmailReminderMessage'),
                action: tNotifications('auth.verifyEmailReminderAction'),
                to: '/account/security',
                icon: <IconMailExclamation size={18} color="#f59e0b" />,
                color: 'yellow',
            });
        }

        const kycFlag = `xver:reminded:kyc:${user.id}`;
        if (user.kyc.level !== KYCLevel.L2 && !sessionStorage.getItem(kycFlag)) {
            sessionStorage.setItem(kycFlag, '1');
            showReminder({
                id: 'reminder-kyc',
                title: tNotifications('auth.verifyKycReminderTitle'),
                message: tNotifications('auth.verifyKycReminderMessage'),
                action: tNotifications('auth.verifyKycReminderAction'),
                to: '/account/profile',
                icon: <IconUserExclamation size={18} color="#f59e0b" />,
                color: 'yellow',
            });
        }
    }, [user?.id, user?.verifyEmailAddress, user?.kyc.level, showReminder, tNotifications]);

    const trialQuery = useTrialCreditQuery();
    useEffect(() => {
        const status = trialQuery.data;
        if (!user || !status) return;
        if (!status.available || !status.kycPassed || status.claimed) return;

        const trialFlag = `xver:reminded:trial-credit:${user.id}`;
        if (sessionStorage.getItem(trialFlag)) return;
        sessionStorage.setItem(trialFlag, '1');

        showReminder({
            id: 'reminder-trial',
            title: tNotifications('trial.availableTitle'),
            message: tNotifications('trial.availableMessage', {
                amount: formatBaht(status.amount),
            }),
            action: tNotifications('trial.claimAction'),
            to: '/account/profile',
            icon: <IconGift size={32} />,
            color: 'teal',
        });
    }, [user?.id, trialQuery.data, showReminder, tNotifications]);

    const navigationLists = useMemo<Array<NavigationListProps>>(
        () => [
            {
                name: t('product.heading'),
                items: [
                    {
                        icon: IconLayoutDashboard,
                        label: t('product.dashboard.label'),
                        to: '/dashboard',
                        description: t('product.dashboard.description'),
                    },
                    {
                        icon: IconCloud,
                        label: t('product.cloudCompute.label'),
                        to: '/cloud-compute',
                        description: t('product.cloudCompute.description'),
                    },
                    {
                        icon: IconServer,
                        label: t('product.bareMetalCompute.label'),
                        to: '/bare-metal-compute',
                        description: t('product.bareMetalCompute.description'),
                    },
                    {
                        icon: IconPackage,
                        label: t('product.containerEngine.label'),
                        to: '/container-engine',
                        description: t('product.containerEngine.description'),
                    },
                    {
                        icon: IconDatabase,
                        label: t('product.blockStorage.label'),
                        to: '/block-storage',
                        description: t('product.blockStorage.description'),
                    },
                    {
                        icon: IconBuildingWarehouse,
                        label: t('product.artifactRegistry.label'),
                        to: '/artifact-registry',
                        description: t('product.artifactRegistry.description'),
                    },
                ],
            },
            {
                name: t('account.heading'),
                items: [
                    {
                        icon: IconUser,
                        label: t('account.profile.label'),
                        to: '/account/profile',
                        description: t('account.profile.description'),
                    },
                    {
                        icon: IconUserShield,
                        label: t('account.security.label'),
                        to: '/account/security',
                        description: t('account.security.description'),
                    },
                    {
                        icon: IconCurrencyBaht,
                        label: t('account.billings.label'),
                        to: '/account/billings',
                        description: t('account.billings.description'),
                    },
                    {
                        icon: IconFolders,
                        label: t('account.projects.label'),
                        to: '/projects',
                        description: t('account.projects.description'),
                        badgeCount: pendingCount,
                        badgeLabel: t('account.projects.pendingRequests', { count: pendingCount }),
                    },
                    {
                        icon: IconKey,
                        label: t('account.sshKeys.label'),
                        to: '/ssh-keys',
                        description: t('account.sshKeys.description'),
                    },
                ],
            },
            {
                name: t('platform.heading'),
                items: [
                    {
                        icon: IconWallet,
                        label: t('platform.topUp.label'),
                        to: '/platform/topup',
                        description: t('platform.topUp.description'),
                    },
                    {
                        icon: IconTicket,
                        label: t('platform.coupon.label'),
                        to: '/platform/coupon',
                        description: t('platform.coupon.description'),
                    },
                    {
                        icon: IconSettings,
                        label: t('platform.settings.label'),
                        to: '/platform/settings',
                        description: t('platform.settings.description'),
                    },
                ],
            },
        ],
        [t, pendingCount]
    );

    return (
        <Fragment>
            <a
                href="#main-content"
                onClick={(event) => {
                    event.preventDefault();
                    mainRef.current?.focus();
                }}
                className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[2000] focus:px-4 focus:py-2 focus:rounded-md focus:bg-fg focus:text-page focus:font-bold"
            >
                {t('layout.skipToContent')}
            </a>
            <Drawer.Root
                id={NAV_DRAWER_ID}
                opened={opened}
                onClose={close}
                returnFocus={returnFocusOnClose}
                size="100%"
                padding="md"
            >
                <Drawer.Overlay />
                <Drawer.Content>
                    <Drawer.Header>
                        <Drawer.Title className="sr-only">{t('layout.menu')}</Drawer.Title>
                        <div className="flex flex-row items-center gap-3">
                            <LogoImage isDrawer />
                            <ColorSchemeToggle />
                        </div>
                        <Drawer.CloseButton aria-label={t('layout.closeMenu')} />
                    </Drawer.Header>
                    <Drawer.Body>
                        <ProjectSwitcher className="mb-4" autoFocus={focusProjectSwitcher} />
                        <Balance onDrawer balance={user?.credit || 0} />
                        <div className="mt-4 w-full flex flex-col items-center overflow-hidden">
                            <nav aria-label={t('layout.mainNavigation')} className="w-full">
                                <NavigationSideBar lists={navigationLists} disableTooltip />
                            </nav>
                            <div className="w-full flex flex-col gap-2">
                                <LanguageSwitcher />
                                <div className="w-full h-9 overflow-hidden">
                                    <ChatSupportButton />
                                </div>
                            </div>
                        </div>
                    </Drawer.Body>
                </Drawer.Content>
            </Drawer.Root>

            <div className="w-full h-dvh flex flex-col justify-start items-stretch bg-page lg:grid lg:grid-cols-[1fr_4fr] lg:items-stretch">
                <div className="w-full h-16 shrink-0 flex items-center justify-between px-2 border-b-[0.5px] border-line bg-surface-2 lg:hidden">
                    <UnstyledButton
                        onClick={handleMenuOpen}
                        aria-label={t('layout.menu')}
                        aria-expanded={opened}
                        aria-controls={NAV_DRAWER_ID}
                        className="flex items-center justify-center"
                    >
                        <div className="t-icon-swap" data-state={opened ? 'a' : 'b'}>
                            <span className="t-icon" data-icon="a">
                                <IconX size={36} />
                            </span>
                            <span className="t-icon" data-icon="b">
                                <IconMenu2 size={36} />
                            </span>
                        </div>
                    </UnstyledButton>
                    <div className="min-w-0 w-full flex flex-row justify-end items-center gap-2">
                        <ProjectChip
                            onClick={handleProjectChipClick}
                            expanded={opened}
                            controls={NAV_DRAWER_ID}
                        />
                        <AccountMenuButton user={user} menuPosition="bottom" />
                    </div>
                </div>
                <aside
                    aria-label={t('layout.menu')}
                    className="w-full max-w-76 h-dvh p-3.5 hidden lg:flex flex-col justify-start items-start border-r-[0.5px] border-line bg-surface-2"
                >
                    <div className="w-full h-full flex flex-col justify-between">
                        <div className="w-full min-h-0 grow flex flex-col">
                            <div className="w-full flex flex-row items-start justify-between">
                                <LogoImage />
                                <div className="mt-4 mr-1">
                                    <ColorSchemeToggle />
                                </div>
                            </div>
                            <div className="w-full my-5 flex flex-col justify-center items-center gap-2">
                                <AccountMenuButton user={user} />
                                <Balance balance={user?.credit || 0} />
                                <ProjectSwitcher />
                            </div>
                            <nav
                                aria-label={t('layout.mainNavigation')}
                                className="w-full min-h-0 grow overflow-hidden"
                            >
                                <NavigationSideBar lists={navigationLists} />
                            </nav>
                        </div>
                        <div className="w-full flex flex-col gap-2">
                            <LanguageSwitcher />
                            <div className="w-full h-9 overflow-hidden">
                                <ChatSupportButton />
                            </div>
                        </div>
                    </div>
                </aside>
                <main
                    id="main-content"
                    ref={mainRef}
                    tabIndex={-1}
                    className="w-full flex-1 min-h-0 lg:h-dvh overflow-y-auto overflow-x-hidden scroll-pb-24"
                >
                    <Container className="p-6 flex flex-col justify-center items-center bg-page sm:p-16">
                        {children}
                    </Container>
                </main>
            </div>
        </Fragment>
    );
}

export default memo(ConsoleLayout);
